import type { FC } from 'react';
import { Fragment, useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { ConditionalWrapper } from '@packages/shared/src/components/ConditionalWrapper/ConditionalWrapper';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { BasicFilterValueListFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import type { BasicFilterValueListFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasicFilterValueListFragmentFragmentDoc';

import { useProgressiveData } from '../../hooks/useProgressiveData';
import { BasicFilterSearchBox } from '../BasicFilterSearchBox/BasicFilterSearchBox';
import { bypassBlockMessages } from './messages';
import { BasicFilterValueItem } from '../BasicFilterValueItem/BasicFilterValueItem';

/* GraphQL */ `
  fragment BasicFilterValueListFragment on SearchFilterBase {
    displayName
    groupedValues {
      groupName
      values {
        id
        name
        ...BasicFilterValueItemFragment
      }
    }
    ... on SearchDefaultFilter {
      isSingleSelection
      isSearchable
    }
  }
`;

const messages = defineMessages({
  basicFilterValueListLabel: {
    id: 'basicFilter.valueList.label',
    defaultMessage: 'Basis-Produktfilter auswählen',
  },
});

export type BasicFilterValueListProps = {
  maskedData: FragmentType<typeof BasicFilterValueListFragmentFragmentDoc>;
  /** Currently selected filter value ids */
  selectedIds: string[];
  /** Callback fired when a filter value is toggled */
  onToggle?: ({
    valueId,
    isSingleSelection,
  }: {
    valueId: string;
    isSingleSelection?: boolean;
  }) => void;
  /** Additional styles which should be applied on the container */
  containerClassName?: string;
};

function useSearchableFilterValues(
  groupedValues: BasicFilterValueListFragmentFragment['groupedValues'],
): {
  filteredGroups: BasicFilterValueListFragmentFragment['groupedValues'];
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
} {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredGroups = searchQuery
    ? groupedValues?.map(({ groupName, values }) => ({
        groupName,
        values: values.filter((value) =>
          value.name.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      }))
    : groupedValues;

  return { filteredGroups, searchQuery, setSearchQuery };
}

const isSkippableGroup = ({ groupName, values }: { values: unknown[]; groupName: string | null }) =>
  !!groupName && values.length > 5;

/**
 * Displays a list of multi-selectable filter values, optionally grouped and/or searchable
 * */
export const BasicFilterValueList: FC<BasicFilterValueListProps> = ({
  maskedData,
  selectedIds,
  containerClassName,
  onToggle,
}) => {
  const filter = unmask<typeof BasicFilterValueListFragmentFragmentDoc>(maskedData);

  const { displayName, groupedValues } = filter;

  const isSingleSelection = 'isSingleSelection' in filter && filter.isSingleSelection;
  const isSearchable = 'isSearchable' in filter && filter.isSearchable;

  const { filteredGroups, searchQuery, setSearchQuery } = useSearchableFilterValues(groupedValues);

  // Progressive rendering: show 1 group initially for better INP, then all groups after mount
  const visibleGroups = useProgressiveData(filteredGroups, 1);

  const handleItemClick = useCallback(
    (valueId: string) => {
      onToggle?.({ valueId, isSingleSelection: isSingleSelection ?? undefined });
    },
    [isSingleSelection, onToggle],
  );

  const { formatMessage } = useIntl();
  const numberOfSkippableGroups = filteredGroups.filter(isSkippableGroup).length;
  const totalFilterItemsCount = filteredGroups.flatMap(({ values }) => values).length;
  const showMasterBypassBlock = totalFilterItemsCount > 5 || numberOfSkippableGroups > 1;

  const uniqueSelectedIds = new Set(selectedIds);

  return (
    <>
      {isSearchable && (
        <BasicFilterSearchBox
          filterName={displayName}
          searchQuery={searchQuery}
          onChange={setSearchQuery}
        />
      )}
      <div className={`overflow-auto ${containerClassName}`}>
        <ConditionalWrapper
          condition={showMasterBypassBlock}
          wrapper={(children) => (
            <BypassBlock
              buttonText={formatMessage(bypassBlockMessages.basicFilterValueList, {
                filterName: displayName,
              })}
              skipScrollIntoView
            >
              {children}
            </BypassBlock>
          )}
        >
          <ul
            className="mt-0.5 flex-1 flex-row p-0"
            aria-label={formatMessage(messages.basicFilterValueListLabel)}
          >
            {visibleGroups?.map((group) => {
              const { groupName, values } = group;

              const renderedGroup = (
                <Fragment key={groupName}>
                  {groupName && values.length ? (
                    <li className="text-color-text-darktransparent list-none px-2 text-[0.875rem] leading-6 font-medium">
                      {groupName}
                    </li>
                  ) : (
                    false
                  )}
                  {values.map((value) => (
                    <BasicFilterValueItem
                      key={value.id}
                      maskedData={value}
                      isSingleSelection={isSingleSelection}
                      onClick={handleItemClick}
                      isSelected={uniqueSelectedIds.has(value.id)}
                    />
                  ))}
                </Fragment>
              );

              // Only show individual bypass if there are multiple groups remaining after filtering, otherwise the master bypass will suffice
              const needsBypassBlock = filteredGroups.length > 1 && isSkippableGroup(group);

              return needsBypassBlock ? (
                <BypassBlock
                  key={group.groupName}
                  buttonText={formatMessage(bypassBlockMessages.basicFilterValueGroup, {
                    filterGroupName: group.groupName,
                  })}
                  skipScrollIntoView
                  className="contents"
                >
                  {renderedGroup}
                </BypassBlock>
              ) : (
                renderedGroup
              );
            })}
          </ul>
        </ConditionalWrapper>
      </div>
    </>
  );
};
